import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Save, Plus, Trash2, Image as ImageIcon, X } from 'lucide-react'
import { parkingSpotApi } from '../services/api'
import api from '../services/api'
import { showError } from '../utils/showError'
import RichTextEditor from '../components/RichTextEditor'
import {
  DetailPageLayout,
  Card,
  CardHeader,
  CardBody,
  LoadingSpinner,
  Button,
  FormField,
  FormSelect,
  FormCheckbox,
  ErrorMessage,
  Badge
} from '../components/common'
import { PARKING_PRESETS } from '../utils/parkingConstants'

function formatTimeDuration(startTime, endTime) {
  if (!startTime || !endTime) return ''
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const totalMinutes = (eh * 60 + em) - (sh * 60 + sm)
  if (totalMinutes <= 0) return ''
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h`
  return `${minutes}m`
}

const CreateParkingSpot = () => {
  const navigate = useNavigate()
  const { parkingSpotId, buildingId: routeBuildingId } = useParams()
  const [searchParams] = useSearchParams()
  const queryBuildingId = searchParams.get('buildingId')
  const isEditing = !!parkingSpotId

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [buildings, setBuildings] = useState([])
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [existingCoverUrl, setExistingCoverUrl] = useState(null)
  const [formData, setFormData] = useState({
    buildingId: routeBuildingId || queryBuildingId || '',
    name: '',
    spotNumber: '',
    floor: '',
    rolesDescription: '',
    startTime: '',
    endTime: '',
    isHandicapAccessible: false,
    isElectricCharging: false,
    isAvailable24_7: false,
    unavailableDates: [],
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    loadBuildings()
    if (isEditing) {
      loadParkingSpotForEdit()
    }
  }, [isEditing, parkingSpotId])

  const loadBuildings = async () => {
    try {
      const response = await api.get('/Building', { params: { pageNumber: 1, pageSize: 200 } })
      setBuildings(response.data.items || [])
    } catch (error) {
      console.error('Error loading buildings:', error)
    }
  }

  const loadParkingSpotForEdit = async () => {
    try {
      setLoading(true)
      const response = await parkingSpotApi.get(parkingSpotId)
      const spot = response.data?.data || response.data

      setFormData({
        buildingId: spot.buildingId || '',
        name: spot.name || '',
        spotNumber: spot.spotNumber || '',
        floor: spot.floor || '',
        rolesDescription: spot.rolesDescription || '',
        startTime: spot.startTime ? spot.startTime.substring(0, 5) : '',
        endTime: spot.endTime ? spot.endTime.substring(0, 5) : '',
        isHandicapAccessible: spot.isHandicapAccessible || false,
        isElectricCharging: spot.isElectricCharging || false,
        isAvailable24_7: spot.isAvailable24_7 || false,
        unavailableDates: (spot.unavailableDates || []).map(ud => ({
          parkingUnavailableDateId: ud.parkingUnavailableDateId,
          from: ud.from ? ud.from.substring(0, 16) : '',
          to: ud.to ? ud.to.substring(0, 16) : '',
          reason: ud.reason || '',
        })),
      })
      if (spot.defaultCoverUrl) {
        setExistingCoverUrl(spot.defaultCoverUrl.startsWith('http') ? spot.defaultCoverUrl : `https://${spot.defaultCoverUrl}`)
      }
    } catch (error) {
      console.error('Error loading parking spot:', error)
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.buildingId) {
      newErrors.buildingId = 'Building is required'
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (formData.name.length > 200) {
      newErrors.name = 'Name must be 200 characters or less'
    }

    if (!formData.isAvailable24_7) {
      if (formData.startTime && formData.endTime && formData.startTime >= formData.endTime) {
        newErrors.endTime = 'End time must be after start time'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const applyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      name: preset.name,
      floor: preset.floor || prev.floor,
      isAvailable24_7: preset.isAvailable24_7,
      startTime: preset.startTime || '',
      endTime: preset.endTime || '',
      isHandicapAccessible: preset.isHandicapAccessible,
      isElectricCharging: preset.isElectricCharging,
      rolesDescription: preset.rolesDescription || '',
    }))
  }

  // Unavailable Date management
  const addUnavailableDate = () => {
    setFormData(prev => ({
      ...prev,
      unavailableDates: [...prev.unavailableDates, { from: '', to: '', reason: '' }],
    }))
  }

  const updateUnavailableDate = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.unavailableDates]
      updated[index] = { ...updated[index], [field]: value }
      return { ...prev, unavailableDates: updated }
    })
  }

  const removeUnavailableDate = (index) => {
    setFormData(prev => ({
      ...prev,
      unavailableDates: prev.unavailableDates.filter((_, i) => i !== index),
    }))
  }

  // Image handling
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image must be less than 10MB' }))
      return
    }
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Only image files are allowed' }))
      return
    }
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    setErrors(prev => ({ ...prev, image: undefined }))
  }

  const removeImage = () => {
    setImageFile(null)
    setImagePreview(null)
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setSubmitting(true)

      const submitData = {
        buildingId: formData.buildingId,
        name: formData.name,
        spotNumber: formData.spotNumber || null,
        floor: formData.floor || null,
        rolesDescription: formData.rolesDescription || null,
        startTime: formData.isAvailable24_7 ? null : (formData.startTime || null),
        endTime: formData.isAvailable24_7 ? null : (formData.endTime || null),
        isHandicapAccessible: formData.isHandicapAccessible,
        isElectricCharging: formData.isElectricCharging,
        isAvailable24_7: formData.isAvailable24_7,
      }

      let savedSpotId = parkingSpotId

      if (isEditing) {
        await parkingSpotApi.update({
          ...submitData,
          parkingSpotId: parseInt(parkingSpotId),
        })
      } else {
        const response = await parkingSpotApi.create(submitData)
        const created = response.data?.data || response.data
        savedSpotId = created.parkingSpotId
      }

      // Upload image if selected
      if (imageFile && savedSpotId) {
        const fd = new FormData()
        fd.append('file', imageFile)
        await parkingSpotApi.uploadImage(savedSpotId, fd)
      }

      // Save new unavailable dates (only for dates without an existing ID)
      const newDates = formData.unavailableDates.filter(ud => !ud.parkingUnavailableDateId && ud.from && ud.to)
      for (const ud of newDates) {
        await parkingSpotApi.addUnavailableDate(savedSpotId, {
          from: new Date(ud.from).toISOString(),
          to: new Date(ud.to).toISOString(),
          reason: ud.reason || null,
        })
      }

      navigate(`/parking/${formData.buildingId}/spots/${savedSpotId}`)
    } catch (error) {
      console.error('Error saving parking spot:', error)
      showError(error)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <LoadingSpinner />

  return (
    <DetailPageLayout
      title={isEditing ? 'Edit Parking Spot' : 'Create Parking Spot'}
      backLink={{ label: 'Back to Parking', href: '/parking' }}
      actions={[
        <Button
          key="save"
          onClick={handleSubmit}
          disabled={submitting}
          variant="primary"
          icon={<Save className="h-4 w-4" />}
          loading={submitting}
        >
          {submitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
        </Button>
      ]}
    >
      <div className="space-y-6">
        {/* Quick Setup Presets (create only) */}
        {!isEditing && (
          <Card>
            <CardHeader title="Quick Setup" subtitle="Start from a preset to save time" />
            <CardBody>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {PARKING_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="p-3 border border-gray-200 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors text-left"
                  >
                    <div className="text-sm font-medium text-gray-900">{preset.name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {preset.isAvailable24_7 ? '24/7' : `${preset.startTime} - ${preset.endTime}`}
                    </div>
                    <div className="flex gap-1 mt-1.5">
                      {preset.isHandicapAccessible && <Badge variant="info" size="small">Accessible</Badge>}
                      {preset.isElectricCharging && <Badge variant="success" size="small">EV</Badge>}
                    </div>
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Basic Details */}
        <Card>
          <CardHeader title="Basic Details" />
          <CardBody className="space-y-6">
            <FormSelect
              label="Building"
              name="buildingId"
              value={formData.buildingId}
              onChange={(e) => handleInputChange('buildingId', e.target.value)}
              error={errors.buildingId}
              disabled={isEditing}
              options={[
                { value: '', label: 'Select a building' },
                ...buildings.map(b => ({
                  value: b.id,
                  label: b.name || b.userName
                }))
              ]}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="Name"
                name="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={errors.name}
                placeholder="Enter parking spot name"
                maxLength={200}
                helpText={`${formData.name.length}/200 characters`}
                required
              />

              <FormField
                label="Spot Number"
                name="spotNumber"
                value={formData.spotNumber}
                onChange={(e) => handleInputChange('spotNumber', e.target.value)}
                placeholder="e.g., A-101"
              />
            </div>

            <FormField
              label="Floor"
              name="floor"
              value={formData.floor}
              onChange={(e) => handleInputChange('floor', e.target.value)}
              placeholder="e.g., P1, B2, Ground"
            />
          </CardBody>
        </Card>

        {/* Operating Hours */}
        <Card>
          <CardHeader title="Operating Hours" />
          <CardBody className="space-y-6">
            <FormCheckbox
              label="Available 24/7"
              name="isAvailable24_7"
              checked={formData.isAvailable24_7}
              onChange={(e) => handleInputChange('isAvailable24_7', e.target.checked)}
            />

            {!formData.isAvailable24_7 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    label="Start Time"
                    name="startTime"
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => handleInputChange('startTime', e.target.value)}
                  />

                  <FormField
                    label="End Time"
                    name="endTime"
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => handleInputChange('endTime', e.target.value)}
                    error={errors.endTime}
                  />
                </div>
                {formData.startTime && formData.endTime && formData.endTime > formData.startTime && (
                  <p className="text-sm text-gray-500 -mt-4">
                    Duration: {formatTimeDuration(formData.startTime, formData.endTime)}
                  </p>
                )}
              </>
            )}
          </CardBody>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader title="Features" />
          <CardBody>
            <div className="space-y-3">
              <FormCheckbox
                label="Handicap Accessible"
                name="isHandicapAccessible"
                checked={formData.isHandicapAccessible}
                onChange={(e) => handleInputChange('isHandicapAccessible', e.target.checked)}
              />

              <FormCheckbox
                label="Electric Vehicle Charging"
                name="isElectricCharging"
                checked={formData.isElectricCharging}
                onChange={(e) => handleInputChange('isElectricCharging', e.target.checked)}
              />
            </div>
          </CardBody>
        </Card>

        {/* Cover Image */}
        <Card>
          <CardHeader title="Cover Image" subtitle="Optional cover image for this parking spot" />
          <CardBody>
            {errors.image && (
              <div className="mb-4">
                <ErrorMessage message={errors.image} />
              </div>
            )}
            <div className="flex items-start gap-6">
              {(imagePreview || existingCoverUrl) && (
                <div className="relative">
                  <img
                    src={imagePreview || existingCoverUrl}
                    alt="Cover preview"
                    className="w-40 h-28 object-cover rounded-lg border border-gray-200"
                  />
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
              <div>
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors text-sm">
                  <ImageIcon className="w-4 h-4 text-gray-500" />
                  <span>{imagePreview || existingCoverUrl ? 'Change Image' : 'Choose Image'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">JPEG, PNG, GIF or WebP. Max 10MB.</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Rules & Conditions */}
        <Card>
          <CardHeader title="Rules & Conditions" subtitle="Display rules and usage conditions to members" />
          <CardBody>
            <RichTextEditor
              content={formData.rolesDescription || ''}
              onChange={(html) => handleInputChange('rolesDescription', html)}
              placeholder="Enter parking spot rules and conditions..."
            />
          </CardBody>
        </Card>

        {/* Unavailable Dates */}
        <Card>
          <CardHeader
            title="Unavailable Dates"
            subtitle="Block out dates when this parking spot is not available"
            action={
              <Button
                onClick={addUnavailableDate}
                variant="secondary"
                size="small"
                icon={<Plus className="w-3 h-3" />}
              >
                Add Date
              </Button>
            }
          />
          <CardBody>
            {formData.unavailableDates.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No unavailable dates. Click "Add Date" to add one.</p>
            ) : (
              <div className="space-y-3">
                {formData.unavailableDates.map((ud, index) => (
                  <div key={index} className="flex items-end gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                      <input
                        type="datetime-local"
                        value={ud.from}
                        onChange={(e) => updateUnavailableDate(index, 'from', e.target.value)}
                        disabled={!!ud.parkingUnavailableDateId}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                      <input
                        type="datetime-local"
                        value={ud.to}
                        onChange={(e) => updateUnavailableDate(index, 'to', e.target.value)}
                        disabled={!!ud.parkingUnavailableDateId}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
                      <input
                        type="text"
                        value={ud.reason}
                        onChange={(e) => updateUnavailableDate(index, 'reason', e.target.value)}
                        disabled={!!ud.parkingUnavailableDateId}
                        placeholder="e.g. Maintenance"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>
                    {!ud.parkingUnavailableDateId && (
                      <Button
                        onClick={() => removeUnavailableDate(index)}
                        variant="ghost"
                        size="small"
                        icon={<Trash2 className="w-4 h-4 text-red-500" />}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </DetailPageLayout>
  )
}

export default CreateParkingSpot
